import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { PaymentMethodModel } from '../../shared/models/payment-method.model';
import { PaymentGatewayModel } from '../../shared/models/payment-gateway.model';
import { Observable } from 'rxjs/Observable';
import { CommonHttpService } from '../../shared/services/http.service';
import { Response } from '@angular/http';
import { InstallmentModel } from '../../shared/models/installment.model';
import { CardModel } from '../../shared/models/card.model';
import { DataLayerService } from '../../shared/data-layer/data-layer.service';

@Injectable()
export class PaymentStepsService {
    private loadingPaymentMethodsSource = new Subject();
    private loadedPaymentMethodsSource = new Subject();
    private loadingPaymentGatewayConfigurationsSource = new Subject();
    private loadedPaymentGatewayConfigurationsSource = new Subject();
    private loadingInstallmentsSource = new Subject();
    private loadedInstallmentsSource = new Subject();
    private loadingCardsSource = new Subject();
    private loadedCardsSource = new Subject();
    private changedPaymentMethodSource = new Subject();

    protected paymentMethods: Array<PaymentMethodModel> = [];
    protected selectedPaymentMethod: PaymentMethodModel = null;

    public loadingPaymentMethods$ = this.loadingPaymentMethodsSource.asObservable();
    public loadedPaymentMethods$ = this.loadedPaymentMethodsSource.asObservable();
    public loadingPaymentGatewayConfigurations$ = this.loadingPaymentGatewayConfigurationsSource.asObservable();
    public loadedPaymentGatewayConfigurations$ = this.loadedPaymentGatewayConfigurationsSource.asObservable();
    public loadingInstallments$ = this.loadingInstallmentsSource.asObservable();
    public loadedInstallments$ = this.loadedInstallmentsSource.asObservable();
    public loadingCards$ = this.loadingCardsSource.asObservable();
    public loadedCards$ = this.loadedCardsSource.asObservable();
    public changedPaymentMethod$ = this.changedPaymentMethodSource.asObservable();

    constructor(
        protected http: CommonHttpService,
        protected dataLayer: DataLayerService
    ) {}

    /**
     * This call API to load all available payment methods.
     **/
    public loadPaymentMethods(): Observable<any> {
        this.loadingPaymentMethodsSource.next(null);
        return new Observable(observable => {
            this.http.useAuth().get(
                '/checkout/payment/payment-methods'
            ).subscribe((res: Response) => {
                this.paymentMethods = this.buildPaymentMethodArrayWithModels(res.json().data);
                this.loadedPaymentMethodsSource.next(this.paymentMethods);
                observable.next(this.paymentMethods);
                observable.complete();
            }, (err: Response) => {
                observable.error(err.json());
            });
        });
    }
    /**
     * 'paymentMethods' property getter.
     **/
    public getPaymentMethods(): Array<PaymentMethodModel> {
        return this.paymentMethods;
    }

    public getSelectedPaymentMethod()  {
        return this.selectedPaymentMethod;
    }

    /**
     * This call API to load all available installments to some payment method.
     **/
    public loadInstallments(paymentMethod: PaymentMethodModel): Observable<Array<InstallmentModel>> {
        this.loadingInstallmentsSource.next();
        return new Observable(observable => {
            this.http.useAuth().get(
                `/checkout/payment/payment-methods/${paymentMethod.id}/installments`
            ).subscribe((res: Response) => {
                const installments: Array<InstallmentModel> = [];
                for (let installment of res.json().data) {
                    installments.push(new InstallmentModel(
                        installment.number,
                        installment.interest,
                        installment.minimum_value,
                        installment.amount,
                        installment.interest_amount,
                        installment.percentage_interest,
                        installment.discount_amount_installment,
                        installment.percentage_discount,
                        installment.id
                    ));
                }
                this.loadedInstallmentsSource.next(installments);
                observable.next(installments);
                observable.complete();
            }, (err: Response) => {
                observable.error(err.json());
            });
        });
    }
    /**
     * This call API to load all cards from current client
     **/
    public loadCards(paymentMethod: PaymentMethodModel): Observable<Array<CardModel>> {
        this.loadingCardsSource.next(null);
        return new Observable(observable => {
            this.http.useAuth().get(`/checkout/payment/payment-methods/${paymentMethod.id}/cards`).subscribe(
                (res: Response) => {
                    let cards = this.buildCardArrayWithModels(res.json().data);
                    this.loadedCardsSource.next(cards);
                    observable.next(cards);
                    observable.complete();
                }, (err: Response) => {
                    observable.error(err.json());
                }
            );
        });
    }
    /**
     *  This call API to load configurations of a payment gateway.
     **/
    public loadConfigurations(
        paymentGateway: PaymentGatewayModel
    ): Observable<PaymentGatewayModel> {
        this.loadingPaymentGatewayConfigurationsSource.next(null);
        return new Observable(observable => {
            this.http
                .useAuth()
                .get(`checkout/payment/payment-gateways/${paymentGateway.id}/configurations`)
                .subscribe((res: Response) => {
                    paymentGateway.fillConfigurations(res.json().data);

                    this.loadedPaymentGatewayConfigurationsSource.next(paymentGateway);
                    observable.next(paymentGateway);
                    observable.complete();
                }, (err: Response) => {
                    observable.error(err.json());
                });
        });
    }
    /**
     * Change selected payment method and emit event.
     **/
    public setSelectedPaymentMethod(paymentMethod: PaymentMethodModel) {
        this.selectedPaymentMethod = paymentMethod;
        this.changedPaymentMethodSource.next(paymentMethod);
        this.dataLayer.selectPaymentMethod(paymentMethod);
    }

    /**
     * This receive a json and create PaymentMethodModel instances using
     * this json.
     **/
    private buildPaymentMethodArrayWithModels(json: any): Array<PaymentMethodModel> {
        let arr: Array<PaymentMethodModel> = [];
        for(let method of json) {
            let model = new PaymentMethodModel();
            model.fill(method);
            arr.push(model);
        }
        return arr;
    }
    /**
     * This receive a json and create CardModel instances using
     * this json.
     **/
    private buildCardArrayWithModels(json: any): Array<CardModel> {
        let arr: Array<CardModel> = [];
        for (let card of json) {
            let model = new CardModel();
            model.parseJson(card);
            arr.push(model);
        }
        return arr;
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src727013012/src/src/app/payment/services/payment-steps.service.ts